import type { NavigationMenuItem } from '#ui/types';
import type { ComputedRef } from 'vue';
import { unref } from 'vue';

import { AssetType, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { Locale } from '~/i18n/i18nConfig';

export type UseNavMenuItemsReturn = {
    navMenuItems: ComputedRef<NavigationMenuItem[]>;
    sellLink: ComputedRef<string>;
};

export const useNavMenuItems = (): UseNavMenuItemsReturn => {
    // COMPOSABLES
    const localePath = useLocalePath();
    const { locale } = useI18n();
    const { tt } = useTypedI18n();
    const { formatSlpRoute } = useSlpRouteUtils();
    const {
        trackMenuDirectSale,
        trackMenuAuction,
        trackMenuSell,
        trackMenuHowToHowSellUsedBus,
        trackMenuReasonsBuyBusAuction,
        trackMenuHowToHowGuideBuyUsedBus,
        trackMenuHowToTrustReport,
        trackMenuFaq,
    } = useTracking();

    const sellLink = computed<string>(() => {
        switch (locale.value) {
            case Locale.IT:
                return 'https://vendi.fleequid.com/';
            case Locale.PL:
                return 'https://vendi.fleequid.com/pl/sprzedawaj';

            default:
                return 'https://vendi.fleequid.com/en/sell';
        }
    });

    return {
        navMenuItems: computed<NavigationMenuItem[]>(() => [
            {
                label: tt('menu.directSale'),
                to: localePath(formatSlpRoute({ sellingMethod: SellingMethod.DirectSale, assetType: AssetType.Bus })),
                onSelect: trackMenuDirectSale,
            },
            {
                label: tt('menu.auction'),
                to: localePath(formatSlpRoute({ sellingMethod: SellingMethod.Auction, assetType: AssetType.Bus })),
                onSelect: trackMenuAuction,
            },
            {
                label: tt('menu.sell'),
                to: unref(sellLink),
                target: '_blank',
                onSelect: trackMenuSell,
            },
            {
                label: tt('menu.howTo.base'),
                children: [
                    {
                        label: tt('menu.howTo.howSellUsedBus'),
                        to: localePath(ROUTE_NAMES.HOW_TO_HOW_SELL_USED_BUS),
                        onSelect: trackMenuHowToHowSellUsedBus,
                    },
                    {
                        label: tt('menu.howTo.reasonsBuyBusAuction'),
                        to: localePath(ROUTE_NAMES.HOW_TO_REASONS_BUY_BUS_AUCTION),
                        onSelect: trackMenuReasonsBuyBusAuction,
                    },
                    {
                        label: tt('menu.howTo.guideBuyUsedBus'),
                        to: localePath(ROUTE_NAMES.HOW_TO_GUIDE_BUY_USED_BUS),
                        onSelect: trackMenuHowToHowGuideBuyUsedBus,
                    },
                    {
                        label: tt('menu.howTo.trustReport'),
                        to: localePath(ROUTE_NAMES.HOW_TO_TRUST_REPORT),
                        onSelect: trackMenuHowToTrustReport,
                    },
                ],
            },
            {
                label: tt('menu.faq'),
                to: localePath(ROUTE_NAMES.FAQ),
                onSelect: trackMenuFaq,
                icon: 'lucide:circle-question-mark',
                ui: {
                    linkLabel: 'lg:hidden',
                },
            },
        ]),
        sellLink,
    };
};
